// client/src/components/pages/LexiStar/NotificationRules/RecipientsEditor.jsx
//
// Recipients list (FR-NTF-002): who — a permission (every user holding it), a
// user, a recipient group, the truck's driver, the trip's dispatcher, an
// external contact or a Telegram group — each with the value it needs.
// Also the member editor of a recipient group (a subset of kinds).

import React from 'react';
import { FaPlus, FaTimes } from 'react-icons/fa';
import ThemedSelect from '../../../common/ThemedSelect';
import { inputClasses } from '../StatusBoard/atoms';
import { RECIPIENT_KINDS, ROLE_SUGGESTIONS } from './ruleLogic';

const userLabel = (u) =>
  [u.first_name, u.last_name].filter(Boolean).join(' ') ||
  u.username ||
  u.email ||
  `User ${u.user_id}`;

function RefInput({ recipient, onChange, groups, users, idPrefix }) {
  const id = `${idPrefix}-ref`;
  switch (recipient.kind) {
    case 'driver':
      return (
        <p className="text-xs text-slate-500 py-2">
          The driver allocated to the truck (or on the trip).
        </p>
      );
    case 'dispatcher':
      return <p className="text-xs text-slate-500 py-2">Whoever created the trip.</p>;
    case 'group':
      return (
        <ThemedSelect
          id={id}
          aria-label="Recipient group"
          value={recipient.ref || ''}
          onChange={(e) => onChange({ ref: e.target.value })}
          className={inputClasses}
        >
          <option value="">Choose a group...</option>
          {groups.map((g) => (
            <option key={g.id} value={g.id}>
              {g.name}
            </option>
          ))}
        </ThemedSelect>
      );
    case 'user':
      return users.length ? (
        <ThemedSelect
          id={id}
          aria-label="User"
          value={recipient.ref ? String(recipient.ref) : ''}
          onChange={(e) => {
            const u = users.find((x) => String(x.user_id) === String(e.target.value));
            onChange({ ref: e.target.value, name: u ? userLabel(u) : undefined });
          }}
          className={inputClasses}
        >
          <option value="">Choose a user...</option>
          {users.map((u) => (
            <option key={u.user_id} value={String(u.user_id)}>
              {userLabel(u)}
            </option>
          ))}
        </ThemedSelect>
      ) : (
        <input
          id={id}
          aria-label="User id"
          value={recipient.ref ?? ''}
          onChange={(e) => onChange({ ref: e.target.value })}
          inputMode="numeric"
          placeholder="User id"
          className={inputClasses}
        />
      );
    case 'role':
      return (
        <div>
          <input
            id={id}
            aria-label="Permission"
            value={recipient.ref ?? ''}
            onChange={(e) => onChange({ ref: e.target.value })}
            placeholder="lexistar.trips.view"
            className={`${inputClasses} font-mono text-xs`}
          />
          <div className="mt-1 flex flex-wrap gap-1">
            {ROLE_SUGGESTIONS.filter((s) => s !== recipient.ref)
              .slice(0, 4)
              .map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => onChange({ ref: s })}
                  className="px-1.5 py-0.5 rounded bg-slate-100 text-[10px] font-mono text-slate-600 hover:bg-slate-200"
                >
                  {s}
                </button>
              ))}
          </div>
        </div>
      );
    default: {
      const meta = RECIPIENT_KINDS.find((k) => k.value === recipient.kind);
      return (
        <input
          id={id}
          aria-label={meta?.label || 'Value'}
          value={recipient.ref ?? ''}
          onChange={(e) => onChange({ ref: e.target.value })}
          placeholder={meta?.hint}
          className={inputClasses}
        />
      );
    }
  }
}

export default function RecipientsEditor({
  value = [],
  onChange,
  kinds = RECIPIENT_KINDS.map((k) => k.value),
  groups = [],
  users = [],
  idPrefix = 'rcp',
  disabled = false,
  label = 'Recipients',
}) {
  const options = RECIPIENT_KINDS.filter((k) => kinds.includes(k.value));
  const set = (i, patch) => onChange(value.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <div className="space-y-2" role="group" aria-label={label}>
      {value.map((r, i) => (
        <div key={i} className="flex flex-col sm:flex-row gap-2 sm:items-start">
          <div className="sm:w-56 flex-shrink-0">
            <ThemedSelect
              aria-label={`${label} ${i + 1} kind`}
              value={r.kind}
              onChange={(e) => set(i, { kind: e.target.value, ref: '', name: undefined })}
              className={inputClasses}
              disabled={disabled}
            >
              {options.map((k) => (
                <option key={k.value} value={k.value}>
                  {k.label}
                </option>
              ))}
            </ThemedSelect>
          </div>
          <div className="flex-1 min-w-0">
            <RefInput
              recipient={r}
              onChange={(patch) => set(i, patch)}
              groups={groups}
              users={users}
              idPrefix={`${idPrefix}-${i}`}
            />
          </div>
          {!disabled && (
            <button
              type="button"
              onClick={() => onChange(value.filter((_, j) => j !== i))}
              aria-label={`Remove ${label.toLowerCase()} ${i + 1}`}
              className="self-start p-2 text-slate-400 hover:text-red-600"
            >
              <FaTimes size={12} />
            </button>
          )}
        </div>
      ))}
      {!disabled && (
        <button
          type="button"
          onClick={() => onChange([...value, { kind: options[0]?.value || 'role', ref: '' }])}
          className="inline-flex items-center gap-1 text-xs font-semibold text-primary-700 hover:underline"
        >
          <FaPlus size={9} /> Add {label === 'Members' ? 'member' : 'recipient'}
        </button>
      )}
    </div>
  );
}
